import {
  STUDY_SET_LIST,
  STUDY_SET_SELECTED,
  STUDY_NOT_LOADED,
  STUDY_UPDATE_EXPORT_STATUS,
  STUDY_SHOW_EXPORT_DETAILS,
  STUDY_HIDE_EXPORT_DETAILS,
  ALL_DATA_SET_LIST,
  STUDY_SET_ONE,
  STUDY_LINKING_DONE,
  STUDY_LINKING_IN_PROGESS,
} from "../action-types";

/**
 * The members defined in this type are directly related to the query that populates the state.
 * Changes to the query that add or remove members should apply the change here as well.
 */
export type Study = {
  colour: string;
  id: string;
  name: string;
  description: string;
  devices: { id: string }[];
  animals: {
    id: string;
    devices: {
      transmitters: {
        displayId: string;
      }[];
    }[];
  }[];
  deployments: { id: string }[];
  files: {
    name: string;
    type: string;
    processing: boolean;
    error: string | null;
  }[];
  hasDevicePositionConflicts: boolean;
  networkMetadata: {
    otn: boolean | null;
    submissions: any[];
  };
  fileCount: number;
  deviceCount: number;
  animalCount: number;
  deploymentCount: number;
};

export type StudyState = {
  isLoaded: boolean;
  studies: Study[];
  selectedId: string | null; // the id of the currently selected study
  selectedStudy: Study | undefined; // reference to the selected study if selected
  exports: any;
  showExportDetailsId: string | null; // the exported studyId to show details for
  newStudyId: string | null;
  filesCount: number;
  devicesCount: number;
  animalsCount: number;
  deploymentsCount: number;
  linkingInProgress: boolean;
};

const initialState: StudyState = {
  isLoaded: false,
  studies: [],
  selectedId: null, // the id of the currently selected study
  selectedStudy: undefined,
  exports: {},
  showExportDetailsId: null, // the exported studyId to show details for
  newStudyId: null,
  filesCount: 0,
  devicesCount: 0,
  animalsCount: 0,
  deploymentsCount: 0,
  linkingInProgress: false,
};

function selectedStudy({
  selectedId,
  studies,
}: {
  selectedId: string | null;
  studies: Study[] | null;
}) {
  return selectedId && studies ? studies.find(s => s.id == selectedId) : undefined;
}

const studyReducer = (state = initialState, action: any = {}): StudyState => {
  switch (action.type) {
    case STUDY_SET_LIST: {
      // keep in consistent order by id
      const studies = action.payload.studies.sort((s1, s2) => (s1.id > s2.id ? 1 : -1)) || [];
      const selectedId = state.selectedId;
      state = {
        ...state,
        isLoaded: true,
        studies,
        // for convenience, set the selected study automatically when fetched
        selectedStudy: selectedStudy({ selectedId, studies }),
      };
      return state;
    }

    case STUDY_SET_SELECTED: {
      const selectedId = action.payload.selectedId;
      return {
        ...state,
        selectedId,
        selectedStudy: selectedStudy({ selectedId, studies: state.studies }),
      };
    }

    case STUDY_NOT_LOADED: {
      return {
        ...state,
        isLoaded: false,
      };
    }

    case STUDY_UPDATE_EXPORT_STATUS: {
      const exports = { ...state.exports };
      const { studyId } = action.payload;
      exports[studyId] = { ...exports[studyId], ...action.payload };
      state = { ...state, exports };
      return state;
    }

    case STUDY_SHOW_EXPORT_DETAILS:
      return { ...state, showExportDetailsId: action.payload.studyId };

    case STUDY_HIDE_EXPORT_DETAILS: {
      // This removes the export details from state as well
      const exports = { ...state.exports };
      delete exports[action.payload.studyId];
      return { ...state, showExportDetailsId: null, exports };
    }

    case ALL_DATA_SET_LIST: {
      return {
        ...state,
        filesCount: action.payload.filesCount,
        devicesCount: action.payload.devicesCount,
        animalsCount: action.payload.animalsCount,
        deploymentsCount: action.payload.deploymentsCount,
      };
    }

    case STUDY_SET_ONE: {
      const { study } = action.payload;
      const studies = [...state.studies];
      const i = studies.findIndex(s => s.id === study.id);
      if (i < 0) {
        studies.push(study);
      } else {
        studies[i] = study;
      }

      return { ...state, studies };
    }

    case STUDY_LINKING_IN_PROGESS: {
      return {
        ...state,
        linkingInProgress: true,
      };
    }

    case STUDY_LINKING_DONE: {
      return {
        ...state,
        linkingInProgress: false,
      };
    }

    default:
      return state;
  }
};

export default studyReducer;
